import React from 'react';
import { X, BookOpen, Sparkle, Clock, MapPin, MusicNote } from '@phosphor-icons/react';
import { useLang, pickLang } from '../contexts/LangContext';

/**
 * Modal showing detailed information about a Spanish level figure
 * (personality, city, or song). Adapts to the selected UI language.
 */
const LevelFigureModal = ({ levelInfo, open, onClose }) => {
  const { lang, t } = useLang();
  if (!open || !levelInfo) return null;

  const initial = (levelInfo.name || '?').charAt(0).toUpperCase();
  const type = levelInfo.type || 'personality';
  const typeIcon = type === 'city' ? MapPin : type === 'song' ? MusicNote : null;
  const typeLabelMap = {
    es: { city: 'Lugar', song: 'Canción', starter: 'Inicio', personality: 'Personalidad' },
    de: { city: 'Ort', song: 'Lied', starter: 'Start', personality: 'Persönlichkeit' },
  };
  const typeLabel = typeLabelMap[lang]?.[type] || typeLabelMap.es.personality;
  const bioText = pickLang(levelInfo, 'bio', lang);
  const factText = pickLang(levelInfo, 'fact', lang);
  const fieldText = pickLang(levelInfo, 'field', lang) || pickLang(levelInfo, 'region', lang);
  const bioHeading = t(`modal.section.bio.${type === 'starter' ? 'personality' : type}`);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="level-figure-modal"
    >
      <div
        className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with colored banner */}
        <div
          className="relative rounded-t-3xl px-8 pt-8 pb-20"
          style={{ background: `linear-gradient(135deg, ${levelInfo.color}, ${levelInfo.color}CC)` }}
        >
          <div className="absolute top-3 right-3 z-10">
            <button
              onClick={onClose}
              type="button"
              className="w-11 h-11 rounded-full bg-white/20 hover:bg-white/40 active:bg-white/50 text-white flex items-center justify-center transition-colors cursor-pointer shadow-lg"
              aria-label="Schließen"
              data-testid="level-modal-close"
            >
              <X size={22} weight="bold" className="pointer-events-none" />
            </button>
          </div>
          <div className="text-white">
            <p className="text-sm font-medium opacity-90 tracking-wider uppercase flex items-center gap-2">
              {t('modal.level')} {levelInfo.level} · {typeLabel}
            </p>
            <h2 className="font-heading text-3xl font-bold mt-1 mb-2">{levelInfo.name}</h2>
            {fieldText && (
              <p className="text-sm opacity-90">{fieldText}</p>
            )}
          </div>
        </div>

        {/* Portrait / icon circle */}
        <div className="relative -mt-14 px-8">
          <div
            className="w-28 h-28 rounded-full bg-white border-4 border-white shadow-xl flex items-center justify-center font-heading font-bold text-5xl mx-auto"
            style={{ color: levelInfo.color }}
          >
            {typeIcon ? React.createElement(typeIcon, { size: 52, weight: 'duotone' }) : initial}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 pt-4">
          {levelInfo.years && (
            <div className="flex items-center justify-center gap-2 text-[#78716C] text-sm mb-5">
              <Clock size={14} />
              <span>{levelInfo.years}</span>
            </div>
          )}

          {bioText && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen size={18} className="text-[#AB1C24]" weight="fill" />
                <h3 className="font-heading font-semibold text-[#292524] text-sm uppercase tracking-wider">
                  {bioHeading}
                </h3>
              </div>
              <p className="text-[#57534E] leading-relaxed text-[15px]">{bioText}</p>
            </div>
          )}

          {factText && (
            <div
              className="rounded-2xl p-4 mb-5 border"
              style={{ backgroundColor: `${levelInfo.color}0D`, borderColor: `${levelInfo.color}33` }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkle size={16} weight="fill" style={{ color: levelInfo.color }} />
                <h4 className="font-heading font-semibold text-[#292524] text-xs uppercase tracking-wider">{t('modal.section.fact')}</h4>
              </div>
              <p className="text-[#57534E] leading-relaxed text-sm italic">{factText}</p>
            </div>
          )}

          <div className="flex items-center justify-between bg-[#FAF9F6] rounded-xl px-4 py-3 text-sm">
            <span className="text-[#78716C]">{t('modal.required')}</span>
            <span className="font-heading font-bold text-[#292524]">{(levelInfo.min_points || 0).toLocaleString(lang === 'de' ? 'de-DE' : 'es-ES')} Pkt</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelFigureModal;
